import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { AdvisorModel, DocumentModel, EventModel } from '@/lib/models';
import { advisorService, documentService, eventsService } from '@/lib/services';
import { useHandleError } from '@/hooks';
import { AsyncPage, Avatar, FeaturedContentTile, LoadingButton } from '@/components';
import { ReactComponent as PlusIcon } from '@/assets/icons/icon-plus-lg.svg';
import { ReactComponent as CheckIcon } from '@/assets/icons/icon-check-lg.svg';

export const AdvisoryTeamDetail = () => {
	const { advisorId } = useParams<{ advisorId?: string }>();
	const handleError = useHandleError();
	const [advisor, setAdvisor] = useState<AdvisorModel>();
	const [followIsLoading, setFollowIsLoading] = useState(false);

	const [publications, setPublications] = useState<DocumentModel[]>([]);
	const [publicationsLoading, setPublicationsLoading] = useState(false);
	const publicationsPageRef = useRef(0);
	const [publicationsTotalCount, setPublicationsTotalCount] = useState(36);

	const [events, setEvents] = useState<EventModel[]>([]);
	const [eventsLoading, setEventsLoading] = useState(false);
	const eventsPageRef = useRef(0);
	const [eventsTotalCount, setEventsTotalCount] = useState(24);

	const fetchAdvisor = useCallback(async () => {
		if (!advisorId) {
			throw new Error('advisorId is undefined.');
		}

		const response = await advisorService.getAdvisorById(advisorId).fetch();
		setAdvisor(response.advisor);
	}, [advisorId]);

	const fetchPublications = useCallback(async () => {
		setPublicationsLoading(true);

		try {
			const response = await documentService
				.getDocuments({
					authorId: advisorId,
					size: '6',
					page: `${publicationsPageRef.current}`,
				})
				.fetch();

			setPublicationsTotalCount(response.totalCount);
			setPublications((previousValue) => [
				...(publicationsPageRef.current === 0 ? [] : previousValue),
				...response.documents,
			]);
		} catch (error) {
			handleError(error);
		} finally {
			setPublicationsLoading(false);
		}
	}, [advisorId, handleError]);

	const fetchEvents = useCallback(async () => {
		setEventsLoading(true);

		try {
			const response = await eventsService
				.getAllEvents({
					authorId: advisorId,
					size: 3,
					page: `${eventsPageRef.current}`,
				})
				.fetch();
			setEventsTotalCount(response.totalCount);
			setEvents((previousValue) => [...(eventsPageRef.current === 0 ? [] : previousValue), ...response.events]);
		} catch (error) {
			handleError(error);
		} finally {
			setEventsLoading(false);
		}
	}, [advisorId, handleError]);

	const handleFollowButtonClick = useCallback(
		async (follow: boolean) => {
			setFollowIsLoading(true);

			try {
				if (!advisor) {
					throw new Error('advisor is undefined.');
				}

				if (!advisorId) {
					throw new Error('advisorId is undefined.');
				}

				await advisorService.followAuthor({ authorId: advisor.advisorId, follow }).fetch();
				const response = await advisorService.getAdvisorById(advisorId).fetch();

				setAdvisor(response.advisor);
			} catch (error) {
				handleError(error);
			} finally {
				setFollowIsLoading(false);
			}
		},
		[advisor, advisorId, handleError]
	);

	const handleShowMorePublicationsButtonClick = () => {
		publicationsPageRef.current += 1;
		fetchPublications();
	};

	const handleShowMoreEventsButtonClick = () => {
		eventsPageRef.current += 1;
		fetchEvents();
	};

	useEffect(() => {
		fetchPublications();
	}, [fetchPublications]);

	useEffect(() => {
		fetchEvents();
	}, [fetchEvents]);

	return (
		<AsyncPage fetchData={fetchAdvisor}>
			<Helmet>
				<title>Hackett Connect - Advisory Team - Advisor</title>
			</Helmet>

			<Container className="py-16">
				<Row>
					<Col lg={3} className="mb-8">
						<Avatar className="mb-8 mx-auto" size={160} url={advisor?.imageUrl} />
						<h3 className="mb-3 text-center">{advisor?.name}</h3>
						<p className="mb-8 text-center">{advisor?.position}</p>
						<div className="d-flex align-items-center justify-content-center">
							{advisor?.userIsFollowing ? (
								<LoadingButton
									isLoading={followIsLoading}
									classNameInner="d-flex align-items-center"
									className="pl-3 mr-1"
									variant="outline-primary"
									onClick={() => handleFollowButtonClick(false)}
								>
									<CheckIcon className="mr-2" width={20} height={20} />
									Following
								</LoadingButton>
							) : (
								<LoadingButton
									isLoading={followIsLoading}
									classNameInner="d-flex align-items-center"
									className="pl-3 mr-1"
									variant="primary"
									onClick={() => handleFollowButtonClick(true)}
								>
									<PlusIcon className="mr-2" width={20} height={20} />
									Follow
								</LoadingButton>
							)}
						</div>
					</Col>
					<Col lg={{ span: 8, offset: 1 }}>
						<section className="mb-8">
							<h2 className="mb-5">About</h2>
							<div dangerouslySetInnerHTML={{ __html: advisor?.description ?? '' }} />
						</section>

						<hr className="mb-8" />

						<section className="mb-8">
							<h2 className="mb-5">Research &amp; Blogs</h2>
							<Row className="mb-2">
								{publications.length === 0 && <p className="mb-6">No Publications</p>}
								{publications.map((i) => (
									<Col key={i.documentId} className="mb-6" sm={12} md={4} lg={4}>
										<FeaturedContentTile
											to={i.relativeUrl}
											imageUrl={i.imageUrl}
											contentType={i.documentTypeName}
											title={i.name}
											description={i.listDescription}
										/>
									</Col>
								))}
							</Row>
							<div className="d-flex align-items-center justify-content-end">
								<span className="mr-5 text-muted">
									Showing 1-{publications.length} of {publicationsTotalCount}
								</span>
								{publications.length < publicationsTotalCount && (
									<LoadingButton
										isLoading={publicationsLoading}
										variant="outline-primary"
										onClick={handleShowMorePublicationsButtonClick}
									>
										Show More
									</LoadingButton>
								)}
							</div>
						</section>

						<hr className="mb-8" />

						<section>
							<h2 className="mb-5">Events</h2>
							<Row className="mb-5">
								{events.length === 0 && <p className="mb-6">No Events</p>}
								{events.map((i) => (
									<Col key={i.eventId} className="mb-3" sm={12} md={4} lg={4}>
										<FeaturedContentTile
											border
											to={`/events/browse-events/${i.eventId}`}
											contentType={i.eventTypeDesc}
											title={i.name}
											bottomText={i.listDescription}
										/>
									</Col>
								))}
							</Row>
							<div className="d-flex align-items-center justify-content-end">
								<span className="mr-5 text-muted">
									Showing 1-{events.length} of {eventsTotalCount}
								</span>
								{events.length < eventsTotalCount && (
									<LoadingButton
										isLoading={eventsLoading}
										variant="outline-primary"
										onClick={handleShowMoreEventsButtonClick}
									>
										Show More
									</LoadingButton>
								)}
							</div>
						</section>
					</Col>
				</Row>
			</Container>
		</AsyncPage>
	);
};
